// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
// to do, and the count of files carrying it is the size of the refactor.
// ============================================================================
// BRAND ASSET SERVICE
// ============================================================================
// Professional service layer for Brand Asset Analysis feature
// Handles API calls for Dashboard Summary, Dashboard Brands, and Forward Analysis

import axios, { AxiosInstance } from 'axios';
import UnifiedAuthStorage from './unified-auth.service';
import ENV from '../config/legacy-env.js';
import ENDPOINTS from '../config/endpoints.js';
import {
  DashboardSummaryResponse,
  DashboardBrandsResponse,
  ForwardAnalysisRequest,
  ForwardAnalysisResponse,
  BrandOfferingsRequest,
  BrandOfferingsResponse,
  UpdateOfferingsRequest,
  BrandCompetitorsRequest,
  BrandCompetitorsResponse,
  CompetitorEntry,
  UpdateCompetitorsRequest,
  ContentPlanRequest,
  ContentPlanResponse,
  ExecutionPlanRequest,
  ExecutionPlanResponse,
  BrandAgentPromptRequest,
  BrandAgentPromptResponse,
  BrandDetailData,
  StatusFilter,
  ApiResponse,
  AnalysedDomain,
  DomainSearchResult,
  BrandSearchResult,
  BrandSearchResponse
} from '../types/brand-asset.types';
import { logger } from '@/shared/observability/logger';
import { API } from '@/config/api';
import { apiClient } from '@/shared/api/apiClient';
import { ApiError } from '@/shared/api/ApiError';

// ============================================================================
// CONFIGURATION
// ============================================================================

const API_BASE_URL = ENV.API_BASE_URL;
const API_TIMEOUT = 60000; // 60 seconds (default)
const FORWARD_API_TIMEOUT = ENV.FORWARD_API_TIMEOUT;

// Get JWT token from UnifiedAuthStorage
const getAuthToken = (): string | null => {
  return UnifiedAuthStorage.getToken();
};

// Get Brand ID from UnifiedAuthStorage
const getBrandId = (): number | any => {
  return UnifiedAuthStorage.getBrandId();
};

// Create axios instance with authentication
const createAuthenticatedAxios = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: ENV.API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add JWT token and Brand ID
  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      const brandId = getBrandId();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (brandId) {
        config.headers['X-Brand-Id'] = brandId.toString();
      }

      // Log in development
      if (import.meta.env.DEV) {
        logger.debug(`[BrandAssetService] ${config.method?.toUpperCase()} ${config.url}`);
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - Handle errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;

     // Handle unauthorized
      if (status === 401) {
        logger.error('[BrandAssetService] Unauthorized - Session expired');
        // UnifiedAuthStorage.clearAuthData();
        // window.dispatchEvent(new CustomEvent('session-expired'));
        // Don't redirect here - let SessionValidator handle it
        // window.location.href = '/auth/login';
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

const axiosInstance = createAuthenticatedAxios();

// ============================================================================
// BRAND ASSET SERVICE CLASS
// ============================================================================

export class BrandAssetService {
  /**
   * Fetch dashboard summary (plan usage + statistics)
   * GET /api/v1/brands/dashboard/summary
   */
  static async getDashboardSummary(): Promise<ApiResponse<DashboardSummaryResponse>> {
    try {
      const data = await apiClient.get<DashboardSummaryResponse>(API.brandDashboardSummary);

      return {
        success: true,
        data,
        message: 'Dashboard summary fetched successfully'
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Dashboard summary failed:', error);
      return {
        success: false,
        error: error instanceof ApiError ? error.message : 'Failed to fetch dashboard summary',
        message: 'Could not load dashboard statistics. Please try again.'
      };
    }
  }

  /**
   * Fetch dashboard brands with filtering and pagination
   * GET /api/v1/brands/dashboard/brands
   *
   * @param params - Query parameters for filtering and pagination
   * @param params.status - Filter by status (all, completed, processing, failed)
   * @param params.page - Page number (0-indexed)
   * @param params.limit - Number of items per page (1-100)
   * @param params.search - Search term for domain/brand name
   */
  static async getDashboardBrands(params: {
    status?: StatusFilter;
    page?: number;
    limit?: number;
    search?: string;
  } = {}): Promise<ApiResponse<DashboardBrandsResponse>> {
    try {
      const { status = 'all', page = 0, limit = 5, search } = params;

      const queryParams: Record<string, unknown> = { page, size: limit };

      // The server takes the enum's own spelling, and no filter at all for "all".
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }
      if (search && search.trim()) {
        queryParams.search = search.trim();
      }

      const data = await apiClient.get<DashboardBrandsResponse>(API.brandDashboardBrands, {
        params: queryParams
      });

      return {
        success: true,
        data,
        message: 'Dashboard brands fetched successfully'
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Dashboard brands failed:', error);
      return {
        success: false,
        error: error instanceof ApiError ? error.message : 'Failed to fetch dashboard brands',
        message: 'Could not load analyzed domains. Please try again.'
      };
    }
  }

  /**
   * Search previously-analyzed brands by name/domain
   * GET /api/brands/search?q={query}
   *
   * Unlike analyzeDomain() (/forward), this reads from the brand records
   * already persisted in the database — includes fields /forward doesn't
   * return, like categoryName/subCategoryName and downloaded asset URLs.
   *
   * @param query - Brand name or domain to search for (e.g., "stratapps.com")
   * @returns The first matching brand, or null if none found
   */
  static async searchBrands(query: string): Promise<ApiResponse<BrandSearchResult | null>> {
    try {
      logger.debug('🔍 [BrandAssetService] Searching brands:', query);

      const response = await axiosInstance.get<BrandSearchResponse>(
        ENDPOINTS.BRANDS.SEARCH,
        { params: { q: query } }
      );

      const result = response.data.content?.[0] ?? null;

      return {
        success: true,
        data: result,
        message: result ? 'Brand found' : 'No matching brand found'
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Brand search failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to search brands',
        message: 'Could not search brands. Please try again.'
      };
    }
  }

  /**
   * Analyze a new domain using the Forward API
   * POST /api/forward
   *
   * Goes through the shared `apiClient`, so the bearer token, the silent refresh and the
   * error translation are the same as everywhere else. The caller is identified by the
   * access token — the body carries only the site, never a user id.
   *
   * @param domain - Domain or URL to analyze (e.g., "apple.com"). A bare domain is given
   *   `https://` by the server.
   * @returns Complete brand analysis data
   */
  static async analyzeDomain(domain: string): Promise<ApiResponse<ForwardAnalysisResponse>> {
    try {
      logger.debug('🔍 [BrandAssetService] Analyzing domain:', domain);

      const payload: ForwardAnalysisRequest = { url: domain.trim() };

      // Extended timeout: an unseen domain is crawled, which can take minutes.
      const data = await apiClient.post<ForwardAnalysisResponse>(API.forward, payload, {
        timeout: FORWARD_API_TIMEOUT,
      });

      logger.debug('✅ [BrandAssetService] Domain analysis completed:', {
        brandName: data.Company?.Name,
        colors: data.Colors?.length || 0,
        fonts: data.Fonts?.length || 0
      });

      return {
        success: true,
        data,
        message: `Successfully analyzed ${domain}`
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Domain analysis failed:', error);

      let errorMessage = 'Failed to analyze domain. Please try again.';
      if (error instanceof ApiError) {
        if (error.status === 0) {
          errorMessage = 'Analysis timed out or the connection was lost. The domain may be unreachable or taking longer than expected.';
        } else if (error.status === 504) {
          errorMessage = 'Analysis timed out. The domain may be unreachable or taking longer than expected.';
        } else if (error.status === 400) {
          errorMessage = error.message || 'Invalid domain. Please check the URL and try again.';
        } else if (error.status === 401) {
          errorMessage = error.message;
        } else if (error.status === 429) {
          // The backend answers 429 when this month's allowance of brand analyses is used. Only a
          // fresh extraction is refused; a brand that is already stored is still answered.
          errorMessage = 'You have used all of your brand analyses for this month. Your allowance starts again '
            + 'on the first of next month. Brands that are already analysed can still be opened.';
        } else if (error.status >= 500) {
          errorMessage = `Unable to analyze "${domain}". Please analyse again.`;
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Suggest what an already-analyzed brand sells / doesn't sell
   * POST /brand/offerings
   *
   * Requires the domain to have already been through analyzeDomain() (/forward) —
   * this reuses that stored description rather than re-scraping. Cached server-side
   * per domain, so repeat calls for the same domain return instantly without
   * re-invoking the LLM.
   *
   * @param domain - Domain to suggest offerings for (e.g., "apple.com")
   */
  static async suggestOfferings(domain: string): Promise<ApiResponse<BrandOfferingsResponse>> {
    try {
      logger.debug('🛍️ [BrandAssetService] Suggesting offerings for domain:', domain);

      const payload: BrandOfferingsRequest = {
        url: domain
      };

      // Java's own timeout for this call (SEO agent web_search + LLM) is 60s — give
      // the client a bit more margin so Java's real error surfaces instead of the
      // browser aborting the connection first with no response at all.
      const response = await axiosInstance.post<BrandOfferingsResponse>(
        ENDPOINTS.BRANDS.OFFERINGS,
        payload,
        { timeout: 65000 }
      );

      logger.debug('✅ [BrandAssetService] Offerings suggested:', {
        sellItems: response.data.sellItems?.length || 0,
        dontSellItems: response.data.dontSellItems?.length || 0,
        cached: response.data.cached
      });

      return {
        success: true,
        data: response.data,
        message: `Successfully suggested offerings for ${domain}`
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Offerings suggestion failed:', error);

      let errorMessage = 'Failed to suggest offerings';
      if (error.response?.status === 404) {
        errorMessage = error.response.data?.error || 'This website hasn\'t been analyzed yet. Please analyze it first.';
      } else if (error.response?.status === 401) {
        errorMessage = error.response.data?.error || error.response.data?.message || 'Your API key has expired. Please upgrade your plan or renew your subscription.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Rate limit exceeded. Please try again later.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Save user-edited (Add/Delete) sell / don't-sell lists as-is, no LLM call.
   * PUT /brand/offerings
   *
   * Requires the domain to have already been through suggestOfferings() at least
   * once (same precondition — brand must exist from /forward).
   *
   * @param domain - Domain the offerings belong to (e.g., "apple.com")
   * @param sellItems - Full updated "what you sell" list
   * @param dontSellItems - Full updated "what you don't sell" list
   */
  static async updateOfferings(
    domain: string,
    sellItems: string[],
    dontSellItems: string[]
  ): Promise<ApiResponse<BrandOfferingsResponse>> {
    try {
      const payload: UpdateOfferingsRequest = {
        url: domain,
        sellItems,
        dontSellItems
      };

      const response = await axiosInstance.put<BrandOfferingsResponse>(
        ENDPOINTS.BRANDS.OFFERINGS,
        payload,
        { timeout: 30000 }
      );

      return {
        success: true,
        data: response.data,
        message: `Successfully saved offerings for ${domain}`
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Offerings update failed:', error);

      let errorMessage = 'Failed to save offerings';
      if (error.response?.status === 404) {
        errorMessage = error.response.data?.error || 'This website hasn\'t been analyzed yet. Please analyze it first.';
      } else if (error.response?.status === 401) {
        errorMessage = error.response.data?.error || error.response.data?.message || 'Your API key has expired. Please upgrade your plan or renew your subscription.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Rate limit exceeded. Please try again later.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Suggest competitors + authority scores for an already-analyzed brand
   * POST /brand/competitors
   *
   * Requires the domain to have already been through analyzeDomain() (/forward), same
   * precondition as suggestOfferings(). Cached server-side per domain.
   *
   * @param domain - Domain to suggest competitors for (e.g., "apple.com")
   */
  static async suggestCompetitors(domain: string): Promise<ApiResponse<BrandCompetitorsResponse>> {
    try {
      logger.debug('🏁 [BrandAssetService] Suggesting competitors for domain:', domain);

      const payload: BrandCompetitorsRequest = {
        url: domain
      };

      // Same reasoning as suggestOfferings — Java's own timeout here is 60s.
      const response = await axiosInstance.post<BrandCompetitorsResponse>(
        ENDPOINTS.BRANDS.COMPETITORS,
        payload,
        { timeout: 65000 }
      );

      logger.debug('✅ [BrandAssetService] Competitors suggested:', {
        count: response.data.competitors?.length || 0,
        cached: response.data.cached
      });

      return {
        success: true,
        data: response.data,
        message: `Successfully suggested competitors for ${domain}`
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Competitors suggestion failed:', error);

      let errorMessage = 'Failed to suggest competitors';
      if (error.response?.status === 404) {
        errorMessage = error.response.data?.error || 'This website hasn\'t been analyzed yet. Please analyze it first.';
      } else if (error.response?.status === 401) {
        errorMessage = error.response.data?.error || error.response.data?.message || 'Your API key has expired. Please upgrade your plan or renew your subscription.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Rate limit exceeded. Please try again later.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Save a user-edited (Add/Delete) competitor list as-is, no LLM call or OpenPageRank lookup.
   * PUT /brand/competitors
   *
   * Requires the domain to have already been through suggestCompetitors() at least
   * once (same precondition — brand must exist from /forward).
   *
   * @param domain - Domain the competitors belong to (e.g., "apple.com")
   * @param competitors - Full updated competitor list
   */
  static async updateCompetitors(
    domain: string,
    competitors: CompetitorEntry[]
  ): Promise<ApiResponse<BrandCompetitorsResponse>> {
    try {
      const payload: UpdateCompetitorsRequest = {
        url: domain,
        competitors
      };

      const response = await axiosInstance.put<BrandCompetitorsResponse>(
        ENDPOINTS.BRANDS.COMPETITORS,
        payload,
        { timeout: 30000 }
      );

      return {
        success: true,
        data: response.data,
        message: `Successfully saved competitors for ${domain}`
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Competitors update failed:', error);

      let errorMessage = 'Failed to save competitors';
      if (error.response?.status === 404) {
        errorMessage = error.response.data?.error || 'This website hasn\'t been analyzed yet. Please analyze it first.';
      } else if (error.response?.status === 401) {
        errorMessage = error.response.data?.error || error.response.data?.message || 'Your API key has expired. Please upgrade your plan or renew your subscription.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Rate limit exceeded. Please try again later.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Generate a content plan (topics + target keywords, scheduled sequentially)
   * for an already-analyzed brand
   * POST /brand/content-plan
   *
   * Requires the domain to have already been through analyzeDomain() (/forward), same
   * precondition as suggestOfferings() / suggestCompetitors(). Cached server-side per domain.
   * Item count is scoped to whatever the configured SEO business goals/prompt call for —
   * not a fixed number.
   *
   * @param domain - Domain to generate a content plan for (e.g., "apple.com")
   */
  static async suggestContentPlan(domain: string): Promise<ApiResponse<ContentPlanResponse>> {
    try {
      logger.debug('🏁 [BrandAssetService] Generating content plan for domain:', domain);

      const payload: ContentPlanRequest = {
        url: domain
      };

      // Java's own timeout for this call is 90s (content plans can be long) —
      // the default 60s client timeout was aborting before Java could ever respond.
      const response = await axiosInstance.post<ContentPlanResponse>(
        ENDPOINTS.BRANDS.CONTENT_PLAN,
        payload,
        { timeout: 95000 }
      );

      logger.debug('✅ [BrandAssetService] Content plan generated:', {
        count: response.data.items?.length || 0,
        cached: response.data.cached
      });

      return {
        success: true,
        data: response.data,
        message: `Successfully generated content plan for ${domain}`
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Content plan generation failed:', error);

      let errorMessage = 'Failed to generate content plan';
      if (error.response?.status === 404) {
        errorMessage = error.response.data?.error || 'This website hasn\'t been analyzed yet. Please analyze it first.';
      } else if (error.response?.status === 401) {
        errorMessage = error.response.data?.error || error.response.data?.message || 'Your API key has expired. Please upgrade your plan or renew your subscription.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Rate limit exceeded. Please try again later.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Generate the full five-phase SEO execution plan (Content Expansion, Technical
   * SEO, Existing Content Optimization, Google Search Console Optimization,
   * Performance Monitoring) for an already-analyzed brand — the SEO roadmap/task
   * plan. Discovery (crawl/audit/keyword research/content-gap analysis) runs
   * during generation itself, not as a scheduled task, so the plan starts
   * directly with Content Expansion. Its tasks are one page or post each
   * (contentType), packed multiple-per-day per publishingCapacity — still not a
   * full title/keyword/date content calendar. For that, use suggestContentPlan()
   * instead — the same one the real content-execution service reads from.
   * POST /brand/execution-plan
   *
   * Requires the domain to have already been through analyzeDomain() (/forward),
   * same precondition as suggestContentPlan(). Cached server-side per domain.
   * Planning only — nothing in the returned plan has been executed yet.
   *
   * @param domain - Domain to generate an execution plan for (e.g., "apple.com")
   */
  static async suggestExecutionPlan(domain: string): Promise<ApiResponse<ExecutionPlanResponse>> {
    try {
      logger.debug('🏁 [BrandAssetService] Generating execution plan for domain:', domain);

      const payload: ExecutionPlanRequest = {
        url: domain
      };

      // Java's own timeout for this call is 600s (the mandatory crawl_website step
      // plus the phase-planning tool loop can legitimately take a while) — give the
      // client extra margin so it isn't the one that aborts first.
      const response = await axiosInstance.post<ExecutionPlanResponse>(
        ENDPOINTS.BRANDS.EXECUTION_PLAN,
        payload,
        { timeout: 605000 }
      );

      logger.debug('✅ [BrandAssetService] Execution plan generated:', {
        phases: response.data.phases?.length || 0,
        cached: response.data.cached
      });

      return {
        success: true,
        data: response.data,
        message: `Successfully generated execution plan for ${domain}`
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Execution plan generation failed:', error);

      let errorMessage = 'Failed to generate execution plan';
      if (error.response?.status === 404) {
        errorMessage = error.response.data?.error || 'This website hasn\'t been analyzed yet. Please analyze it first.';
      } else if (error.response?.status === 401) {
        errorMessage = error.response.data?.error || error.response.data?.message || 'Your API key has expired. Please upgrade your plan or renew your subscription.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Rate limit exceeded. Please try again later.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Generates the SEO agent's standing operating prompt — grounded in the brand's
   * sell/don't-sell items, competitors, and the caller's saved business goals, all
   * resolved server-side. Always regenerates fresh (no caching) and is persisted
   * server-side into the caller's saved AgentConfiguration.
   *
   * Java's own timeout for this call is 75s (it proxies to the Python agent) —
   * mirroring suggestContentPlan's generous client-side timeout so we don't abort
   * before Java can respond.
   */
  static async suggestAgentPrompt(domain: string): Promise<ApiResponse<BrandAgentPromptResponse>> {
    try {
      logger.debug('🏁 [BrandAssetService] Generating agent prompt for domain:', domain);

      const payload: BrandAgentPromptRequest = {
        url: domain
      };

      const response = await axiosInstance.post<BrandAgentPromptResponse>(
        ENDPOINTS.BRANDS.AGENT_PROMPT,
        payload,
        { timeout: 80000 }
      );

      logger.debug('✅ [BrandAssetService] Agent prompt generated');

      return {
        success: true,
        data: response.data,
        message: `Successfully generated agent prompt for ${domain}`
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Agent prompt generation failed:', error);

      let errorMessage = 'Failed to generate the SEO agent prompt';
      if (error.response?.status === 404) {
        errorMessage = error.response.data?.error || 'This website hasn\'t been analyzed yet. Please analyze it first.';
      } else if (error.response?.status === 401) {
        errorMessage = error.response.data?.error || error.response.data?.message || 'Your API key has expired. Please upgrade your plan or renew your subscription.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Rate limit exceeded. Please try again later.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Get brand details by brandId
   * GET ${ENV.API_BASE_URL}/api/brands/dashboard/brands/{brandId}/details
   * 
   * @param brandId - The brandId of analyzed brand (or searchId for backward compatibility)
   * @returns Complete brand analysis data (same structure as Forward API)
   * @note Returns ForwardAnalysisResponse structure with all brand assets
   */
  static async getBrandDetails(brandId: string | number): Promise<ApiResponse<ForwardAnalysisResponse>> {
    try {
      logger.debug(' [BrandAssetService] Fetching brand details for brandId:', brandId);

      // Use ENV.API_BASE_URL instead of hardcoded staging URL
      const url = `${ENV.API_BASE_URL}/api/v1/brands/dashboard/brands/${brandId}/details`;
      
      // Get auth token
      const token = getAuthToken();
      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.get<ForwardAnalysisResponse>(url, { headers });

      logger.debug(' [BrandAssetService] Brand details received:', {
        brandName: response.data.Company.Name,
        brandId
      });

      return {
        success: true,
        data: response.data,
        message: 'Brand details fetched successfully'
      };
    } catch (error: any) {
      logger.error(' [BrandAssetService] Failed to fetch brand details:', error);

      let errorMessage = 'Failed to fetch brand details';
      if (error.response?.status === 404) {
        errorMessage = 'Brand not found. It may have been deleted.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Get brand information by brandId using new endpoint
   * GET /api/brands/{brandId}
   * 
   * @param brandId - The brandId of the brand
   * @returns Complete brand information with assets, colors, fonts, etc.
   */
  static async getBrandInfo(brandId: string | number): Promise<ApiResponse<any>> {
    try {
      logger.debug('🔎 [BrandAssetService] Fetching brand info for brandId:', brandId);

      const response = await axiosInstance.get<any>(
        `${ENDPOINTS.BRANDS.BRAND_INFO}/${brandId}`
      );

      logger.debug('✅ [BrandAssetService] Brand info received:', {
        brandName: response.data.name,
        brandId,
        assetsCount: response.data.assets?.length || 0,
        colorsCount: response.data.colors?.length || 0,
        fontsCount: response.data.fonts?.length || 0
      });

      return {
        success: true,
        data: response.data,
        message: 'Brand info fetched successfully'
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Failed to fetch brand info:', error);
      logger.error('❌ [BrandAssetService] Error response:', error.response?.data);
      logger.error('❌ [BrandAssetService] Error status:', error.response?.status);
      logger.error('❌ [BrandAssetService] Full URL:', `${ENDPOINTS.BRANDS.BRAND_INFO}/${brandId}`);

      let errorMessage = 'Failed to fetch brand info';
      if (error.response?.status === 404) {
        errorMessage = 'Brand not found. It may have been deleted.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Internal server error. The API may be experiencing issues.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = `Server error: ${error.response.data.errors}`;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Re-analyses a stored brand: the server asks the extraction service about its website again
   * and updates the brand.
   * POST /api/v1/brands/{brandId}/reanalyze
   *
   * Any signed-in user may do this. The caller is the token's, so the request has no body and no
   * user id. Slow, like `analyzeDomain`, so it takes the same extended timeout.
   *
   * @param brandId - The brand to refresh, from the analysed-domains list
   * @returns The refreshed brand, in the shape `analyzeDomain` answers
   */
  static async reanalyzeBrand(brandId: string | number): Promise<ApiResponse<ForwardAnalysisResponse>> {
    try {
      logger.debug('🔄 [BrandAssetService] Re-analysing brand:', brandId);

      const data = await apiClient.post<ForwardAnalysisResponse>(API.brandReanalyze(brandId), undefined, {
        timeout: FORWARD_API_TIMEOUT,
      });

      return { success: true, data, message: 'Brand re-analysed' };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Re-analysis failed:', error);

      let errorMessage = 'Failed to re-analyse the brand. Please try again.';
      if (error instanceof ApiError) {
        if (error.status === 0 || error.status === 504) {
          errorMessage = 'Re-analysis timed out. The domain may be unreachable or taking longer than expected.';
        } else if (error.status === 409) {
          errorMessage = 'This brand is already being re-analysed. Please wait for it to finish.';
        } else if (error.status === 404) {
          errorMessage = 'This brand no longer exists.';
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      return { success: false, error: errorMessage, message: errorMessage };
    }
  }

  /**
   * The reviews scraped from the review platforms for a brand.
   * GET /api/v1/brands/{brandId}/reviews/scraped
   *
   * Never throws: the caller treats a failure as "nothing usable yet" and shows its own
   * "reanalyse" prompt after its timeout. The review scraping endpoints are not part of the
   * new backend yet, so until they are this answers `success: false` and the platform
   * reviews stay empty.
   */
  static async getScrapedReviews(brandId: string | number): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.get<any>(`${ENDPOINTS.BRANDS.BRAND_INFO}/${brandId}/reviews/scraped`, {
        // The server holds this request for up to 150 seconds while a scrape is still storing its
        // results, so the shared 15 second timeout aborted it every time and it showed as failed.
        timeout: 180000,
      });
      return { success: true, data: response.data, message: 'Scraped reviews fetched successfully' };
    } catch (error: any) {
      logger.warn('[BrandAssetService] Scraped reviews unavailable:', error.response?.status ?? error.message);
      return {
        success: false,
        error: 'Scraped reviews are not available.',
        message: 'Scraped reviews are not available.'
      };
    }
  }

  /**
   * Asks the server to scrape a brand's reviews afresh.
   * POST /api/v1/brands/{brandId}/scrape-reviews
   *
   * Never throws, for the same reason as `getScrapedReviews`.
   */
  static async scrapeReviews(brandId: string | number): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.post<any>(`${ENDPOINTS.BRANDS.BRAND_INFO}/${brandId}/scrape-reviews`, undefined, {
        // Scraping waits on the review sites, which takes minutes.
        timeout: FORWARD_API_TIMEOUT,
      });
      return { success: true, data: response.data, message: 'Review scraping started' };
    } catch (error: any) {
      logger.warn('[BrandAssetService] Review scraping unavailable:', error.response?.status ?? error.message);
      return { success: false, error: 'Review scraping is not available.', message: 'Review scraping is not available.' };
    }
  }

  /**
   * Get brand reviews by brandId
   * GET /api/v1/reviews/brands/{brandId}
   * 
   * @param brandId - The brandId of the brand
   * @returns Brand reviews and ratings data
   */
  static async getBrandReviews(brandId: string | number): Promise<ApiResponse<any>> {
    try {
      logger.debug('🔎 [BrandAssetService] Fetching brand reviews for brandId:', brandId);

      const response = await axiosInstance.get<any>(
        `${ENDPOINTS.BRANDS.REVIEWS}/${brandId}`
      );

      logger.debug('✅ [BrandAssetService] Brand reviews received:', {
        brandId,
        reviewsCount: response.data?.reviews?.length || 0,
        averageRating: response.data?.averageRating || 'N/A'
      });

      return {
        success: true,
        data: response.data,
        message: 'Brand reviews fetched successfully'
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Failed to fetch brand reviews:', error);
      logger.error('❌ [BrandAssetService] Error response:', error.response?.data);
      logger.error('❌ [BrandAssetService] Error status:', error.response?.status);
      logger.error('❌ [BrandAssetService] Full URL:', `${ENDPOINTS.BRANDS.REVIEWS}/${brandId}`);

      let errorMessage = 'Failed to fetch brand reviews';
      if (error.response?.status === 404) {
        errorMessage = 'No reviews found for this brand.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Internal server error. The API may be experiencing issues.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = `Server error: ${error.response.data.errors}`;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Download brand kit as a ZIP file
   * GET /api/brands/download-kit?brandId=123
   * GET /api/brands/download-kit?website=https://example.com
   *
   * @param brandId - Identifier of the brand (numeric ID or website URL)
   */
  static async downloadBrandKit(brandId: string | number): Promise<ApiResponse<Blob>> {
    if (!brandId) {
      return {
        success: false,
        error: 'Missing brand identifier',
        message: 'Cannot download brand kit without a valid brand ID'
      };
    }

    const isNumericId = typeof brandId === 'number' || /^\d+$/.test(brandId.toString());
    
    const queryParam = isNumericId 
      ? `brandId=${brandId}`
      : `website=${encodeURIComponent(brandId.toString())}`;
    
    const url = `${ENDPOINTS.BRANDS.DOWNLOAD_KIT}?${queryParam}`;

    try {
      logger.debug('⬇️ [BrandAssetService] Downloading brand kit for:', brandId);
      logger.debug('⬇️ [BrandAssetService] Request URL:', url);

      const response = await axiosInstance.get<ArrayBuffer>(url, {
        responseType: 'arraybuffer',
        headers: {
          Accept: 'application/zip,application/octet-stream'
        }
      });

      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      const suggestedFileName = contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1];

      const blob = new Blob([response.data], { type: 'application/zip' });

      return {
        success: true,
        data: blob,
        message: suggestedFileName || ''
      };
    } catch (error: any) {
      logger.error('❌ [BrandAssetService] Brand kit download failed:', error);

      let errorMessage = 'Failed to download brand kit';
      if (error.response?.status === 404) {
        errorMessage = 'Brand kit not found for this brand';
      } else if (error.response?.status === 401) {
        errorMessage = 'Failed to Fetch the brand details.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to download this brand kit';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many download requests. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  // ============================================================================
  // TRANSFORMATION UTILITIES
  // ============================================================================

  /**
   * Transform API DomainSearchResult to component AnalysedDomain
   * Normalizes status to lowercase and structures data for UI
   */
  static transformToAnalysedDomain(apiDomain: DomainSearchResult): AnalysedDomain {
    // Normalize status to lowercase
    const status = apiDomain.status.toLowerCase() as 'completed' | 'processing' | 'failed';

    // Generate logo URL (use Clearbit as fallback or API provided logo)
    const logoUrl = apiDomain.logos?.[0]?.url;
    const primaryLogo = logoUrl 
      ? (logoUrl.startsWith('http') ? logoUrl : `${window.location.origin}${logoUrl}`)
      : `https://logo.clearbit.com/${apiDomain.domain}`;

    // Transform colors from API response
    const colors = (apiDomain.colors || []).slice(0, 5).map(c => ({
      hex: c.hex,
      name: c.name || 'Color',
      rgb: c.rgb || '',
      usage: (c.usage || 'primary') as 'primary' | 'secondary' | 'accent' | 'neutral'
    }));

    // Transform fonts from API response
    const fonts = (apiDomain.fonts || []).slice(0, 3).map(f => ({
      name: f.name,
      type: (f.type === 'google' ? 'google' : f.type === 'system' ? 'system' : 'custom') as 'system' | 'google' | 'custom',
      weights: f.weights || this.extractWeights(f.stack || '')
    }));

    return {
      id: apiDomain.searchId,
      searchId: apiDomain.searchId,
      brandId: apiDomain.brandId,
      domain: apiDomain.domain,
      brandName: apiDomain.brandName,
      status,
      analysedAt: this.formatRelativeTime(apiDomain.searchedAt),
      isInCollection: apiDomain.isInCollection,
      collectionName: apiDomain.collectionName,
      responseRate: apiDomain.responseRate,
      averageRating: apiDomain.averageRating,
      reviewCount: apiDomain.reviewCount,
      monthlyVisits: apiDomain.monthlyVisits,
      employees: apiDomain.employees,
      logos: {
        primary: primaryLogo,
        store_path:apiDomain.logos?.[0]?.store_path,
        secondary: apiDomain.logos?.[1]?.url || null,
        icon: apiDomain.logos?.find(l => l.type === 'icon' || l.type === 'favicon')?.url || primaryLogo,
        favicon: apiDomain.logos?.find(l => l.type === 'favicon')?.url || null
      },
      colors,
      fonts,
      assets: {
        total: apiDomain.totalAssets,
        logos: apiDomain.logosExtracted,
        colors: apiDomain.colorsFound,
        fonts: apiDomain.fontsFound
      },
      errorMessage: apiDomain.errorMessage
    };
  }

  /**
   * Transform Forward API response to BrandDetailData for detail page
   */
  static transformToBrandDetailData(
    apiResponse: ForwardAnalysisResponse,
    searchId: string,
    brandId: any
  ): BrandDetailData {
    const { Logo, Colors, Fonts, Images, Company, _performance, _sessionId } = apiResponse;

    // Calculate asset counts
    const logoCount = Object.values(Logo).filter(v => v && v !== '').length;
    const colorCount = Colors?.length || 0;
    const fontCount = Fonts?.length || 0;
    const imageCount = Images?.length || 0;

    // Transform colors with usage classification
    // Filter out null/invalid colors first
    const validColors = Colors?.filter(color => color.hex && color.rgb) || [];
    
    const transformedColors = validColors.map((color, index) => {
      const brightness = color.brightness || 0;

      // Classify usage based on brightness and position
      let usage: 'primary' | 'secondary' | 'accent' | 'neutral' = 'neutral';
      if (index === 0) usage = 'primary';
      else if (brightness > 200) usage = 'secondary';
      else if (brightness < 50) usage = 'primary';
      else usage = 'accent';

      return {
        hex: color.hex,
        name: color.name || `Color ${index + 1}`,
        rgb: color.rgb,
        hsl: this.rgbToHsl(color.rgb),
        usage,
        brightness: color.brightness
      };
    });

    // Transform fonts
    const transformedFonts = Fonts?.map((font, index) => ({
      name: font.name,
      type: this.detectFontType(font.type),
      weights: this.extractWeights(font.stack),
      category: this.detectFontCategory(font.name),
      usage: index === 0 ? ('heading' as const) : ('body' as const),
      stack: font.stack,
      fallbacks: font.stack.split(',').map(f => f.trim()).slice(1)
    })) || [];

    // Transform logo variants
    const logoVariants = [];
    if (Logo.Logo) {
      logoVariants.push({
        type: 'primary' as const,
        url: Logo.Logo,
        description: 'Primary logo',
        usage: 'Main brand identity, headers, official documents',
        format: this.extractImageFormat(Logo.Logo)
      });
    }
    if (Logo.Symbol) {
      logoVariants.push({
        type: 'symbol' as const,
        url: Logo.Symbol,
        description: 'Brand symbol',
        usage: 'Icon applications, social media',
        format: this.extractImageFormat(Logo.Symbol)
      });
    }
    if (Logo.Icon) {
      logoVariants.push({
        type: 'icon' as const,
        url: Logo.Icon,
        description: 'Brand icon',
        usage: 'Favicon, app icon, small displays',
        format: this.extractImageFormat(Logo.Icon)
      });
    }
    if (Logo.Favicon) {
      logoVariants.push({
        type: 'favicon' as const,
        url: Logo.Favicon,
        description: 'Favicon',
        usage: 'Browser tab icon',
        format: this.extractImageFormat(Logo.Favicon)
      });
    }
    if (Logo.Banner) {
      logoVariants.push({
        type: 'banner' as const,
        url: Logo.Banner,
        description: 'Brand banner',
        usage: 'Website headers, promotional materials',
        format: this.extractImageFormat(Logo.Banner)
      });
    }
    if (Logo.LinkedInLogo) {
      logoVariants.push({
        type: 'linkedin-logo' as const,
        url: Logo.LinkedInLogo,
        description: 'LinkedIn logo',
        usage: 'LinkedIn company profile picture',
        format: this.extractImageFormat(Logo.LinkedInLogo)
      });
    }
    if (Logo.LinkedInBanner) {
      logoVariants.push({
        type: 'linkedin-banner' as const,
        url: Logo.LinkedInBanner,
        description: 'LinkedIn banner',
        usage: 'LinkedIn company cover image',
        format: this.extractImageFormat(Logo.LinkedInBanner)
      });
    }

    // Transform images
    const transformedImages = Images?.map(img => ({
      type: this.detectImageType(img.src),
      url: img.src,
      description: img.alt || 'Brand image',
      dimensions: undefined,
      format: this.extractImageFormat(img.src)
    })) || [];

    // Transform social links
    const socialLinks = Company.SocialLinks
      ? Object.entries(Company.SocialLinks).map(([platform, url]) => ({
        platform: this.capitalizePlatform(platform),
        url
      }))
      : [];

    const sanitizedWebsite = this.sanitizeWebsiteUrl(Company.Website);
    const extractedDomain = sanitizedWebsite
      ? sanitizedWebsite.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '').split('/')[0]
      : '';

    return {
      id: `${brandId}`,
      searchId,
      brandId,
      domain: extractedDomain,
      brandName: Company.Name || 'Unknown Brand',
      description: Company.Description || '',
      logo: Logo.Logo || Logo.Icon || Logo.Symbol || `https://logo.clearbit.com/${extractedDomain}`,
      website: sanitizedWebsite,
      industry: Company.Industry || 'Not specified',
      location: Company.Location || Company.Headquarters || 'Not specified',
      founded: Company.Founded || 'Not specified',
      companyType: Company.CompanyType || Company.Type || 'Not specified',
      employees: Company.Employees || Company.CompanySize || 'Not specified',
      headquarters: Company.Headquarters || Company.Location || 'Not specified',
      type: Company.Type || Company.CompanyType || 'Not specified',
      specialties: Company.Specialties || [],
      locations: Company.Locations || [],
      status: 'completed',
      analysedAt: this.formatDateTime(_performance.timestamp),
      colors: transformedColors,
      logos: {
        primary: Logo.Logo || Logo.Icon || `https://logo.clearbit.com/${extractedDomain}`,
        secondary: Logo.Symbol || undefined,
        icon: Logo.Icon || Logo.Favicon || undefined,
        favicon: Logo.Favicon || undefined,
        banner: Logo.Banner || undefined,
        linkedInLogo: Logo.LinkedInLogo || undefined,
        linkedInBanner: Logo.LinkedInBanner || undefined,
        variants: logoVariants
      },
      fonts: transformedFonts,
      images: transformedImages,
      assets: {
        total: logoCount + colorCount + fontCount + imageCount,
        logos: logoCount,
        colors: colorCount,
        fonts: fontCount,
        images: imageCount
      },
      socialLinks,
      performance: _performance,
      sessionId: _sessionId,
      rawData: apiResponse
    };
  }

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Normalize domain URL to clean format
   */
  private static normalizeDomain(domain: string): string {
    return domain
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '')
      .split('/')[0];
  }

  /**
   * Sanitize website URL by removing newlines and extra text
   */
  private static sanitizeWebsiteUrl(website: string | null | undefined): string {
    if (!website || typeof website !== 'string') {
      return '';
    }

    return website
      .split('\n')[0]
      .trim()
      .replace(/\s+External\s+link.*$/i, '')
      .trim();
  }

  /**
   * Convert RGB string to HSL format
   */
  private static rgbToHsl(rgb: string | null | undefined): string {
    // Handle null, undefined, or empty string
    if (!rgb || typeof rgb !== 'string') {
      return '0, 0%, 0%';
    }
    
    const match = rgb.match(/\d+/g);
    if (!match || match.length < 3) return '0, 0%, 0%';

    const r = parseInt(match[0]) / 255;
    const g = parseInt(match[1]) / 255;
    const b = parseInt(match[2]) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }

    return `${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%`;
  }

  /**
   * Detect font type from string
   */
  private static detectFontType(type: string): 'system' | 'google' | 'custom' {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('system')) return 'system';
    if (lowerType.includes('google')) return 'google';
    return 'custom';
  }

  /**
   * Extract font weights (default common weights)
   */
  static extractWeights(stack: string): string[] {
    return ['300', '400', '500', '600', '700'];
  }

  /**
   * Detect font category from name
   */
  private static detectFontCategory(name: string): 'serif' | 'sans-serif' | 'monospace' | 'display' {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('serif') && !lowerName.includes('sans')) return 'serif';
    if (lowerName.includes('mono') || lowerName.includes('code')) return 'monospace';
    if (lowerName.includes('display')) return 'display';
    return 'sans-serif';
  }

  /**
   * Detect image type from URL
   */
  private static detectImageType(src: string): 'banner' | 'product' | 'marketing' | 'social' {
    const lowerSrc = src.toLowerCase();
    if (lowerSrc.includes('banner')) return 'banner';
    if (lowerSrc.includes('product')) return 'product';
    if (lowerSrc.includes('social') || lowerSrc.includes('og_') || lowerSrc.includes('og-')) return 'social';
    return 'marketing';
  }

  /**
   * Extract image format from URL
   */
  private static extractImageFormat(src: string): string | undefined {
    const match = src.match(/\.(jpg|jpeg|png|gif|svg|webp)($|\?)/i);
    return match ? match[1].toUpperCase() : undefined;
  }

  /**
   * Capitalize platform name
   */
  private static capitalizePlatform(platform: string): string {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  }

  /**
   * Format ISO timestamp to relative time (e.g., "2 hours ago")
   */
  private static formatRelativeTime(isoDate: string): string {
    try {
      const date = new Date(isoDate);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffSecs < 60) return 'Just now';
      if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
      if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;

      return date.toLocaleDateString();
    } catch {
      return 'Recently';
    }
  }

  /**
   * Format ISO timestamp to readable date-time
   */
  private static formatDateTime(isoDate: string): string {
    try {
      const date = new Date(isoDate);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  }
}